/**
 * Mission Panel - Document Upload Modal
 * 
 * A modal panel for uploading and managing documents that provide
 * context to the AI assistant during voice conversations.
 */

import { useState, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X,
  Upload,
  FileText,
  FileSpreadsheet,
  Image as ImageIcon,
  File,
  Trash2,
  AlertTriangle,
  Check,
  Loader2,
  FolderOpen,
} from 'lucide-react';
import { cn } from '@/lib/cn';
import { useDocumentContextStore, type UploadedDocument } from '@/stores/documentContextStore';
import { 
  getFileType, 
  formatFileSize,
  type SupportedFileType 
} from '@/utils/documentProcessor';
import { api } from '@/lib/api';

interface MissionPanelProps {
  isOpen: boolean;
  onClose: () => void;
  onImageUpload?: (file: File) => void; // For sending images directly to Gemini
  onDocumentAdded?: () => void; // BUG-021: Callback to send context to active voice session
}

export function MissionPanel({ isOpen, onClose, onImageUpload, onDocumentAdded }: MissionPanelProps) {
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingFile, setProcessingFile] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  const { 
    documents, 
    totalCharacters, 
    maxCharacters,
    addDocument, 
    removeDocument, 
    clearAllDocuments 
  } = useDocumentContextStore();

  const handleFileSelect = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    
    setError(null);
    
    for (const file of Array.from(files)) {
      const fileType = getFileType(file.name);
      
      if (!fileType) {
        setError(`Unsupported file: ${file.name}`);
        continue;
      }
      
      // Process ALL files via backend (including images for OCR)
      // Images will have text extracted via Gemini Vision OCR
      setIsProcessing(true);
      setProcessingFile(file.name);
      
      try {
        // Upload file to backend for processing
        const formData = new FormData();
        formData.append('file', file);
        
        const response = await api.post('/documents/process', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
        
        const result = response.data;
        
        if (!result.success) {
          setError(`Failed to process "${file.name}": ${result.error}`);
          continue;
        }
        
        // Check character limit
        if (totalCharacters + result.character_count > maxCharacters) {
          setError(`Adding "${file.name}" would exceed the ${(maxCharacters / 1000).toFixed(0)}k character limit. Please remove some documents first.`);
          continue;
        }
        
        addDocument({
          id: `${Date.now()}-${file.name}`,
          name: file.name,
          type: fileType as SupportedFileType,
          size: file.size,
          uploadedAt: new Date(),
          extractedText: result.extracted_text,
          pageCount: result.page_count,
        });

        // BUG-021: Send context to active voice session
        if (onDocumentAdded) {
          onDocumentAdded();
        }

        // For images: also send to Gemini for visual analysis (in addition to OCR text)
        if (fileType === 'image' && onImageUpload) {
          onImageUpload(file);
        }

      } catch (err: any) {
        console.error('Failed to process file:', err);
        const errorMessage = err.response?.data?.detail || err.message || 'Unknown error';
        setError(`Failed to process "${file.name}": ${errorMessage}`);
      }
    }
    
    setIsProcessing(false);
    setProcessingFile(null);
    
    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, [addDocument, totalCharacters, maxCharacters, onImageUpload, onDocumentAdded]);

  const getFileTypeIcon = (type: SupportedFileType) => {
    switch (type) {
      case 'pdf':
      case 'docx':
      case 'txt':
        return <FileText className="w-5 h-5" />;
      case 'xlsx':
      case 'csv':
        return <FileSpreadsheet className="w-5 h-5" />;
      case 'image':
        return <ImageIcon className="w-5 h-5" />;
      default:
        return <File className="w-5 h-5" />;
    }
  };

  const getFileTypeColor = (type: SupportedFileType) => {
    switch (type) {
      case 'pdf':
        return 'text-red-400';
      case 'docx':
      case 'txt':
        return 'text-blue-400';
      case 'xlsx':
      case 'csv':
        return 'text-emerald-400';
      case 'image':
        return 'text-purple-400';
      default:
        return 'text-slate-400';
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          className="bg-slate-900 w-full max-w-2xl rounded-2xl shadow-2xl border border-white/10 overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="p-6 border-b border-white/10 flex justify-between items-center">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-xl bg-gradient-to-br from-violet-500/20 to-fuchsia-500/20 border border-violet-500/30">
                <FolderOpen className="w-6 h-6 text-violet-400" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">Mission Context</h2>
                <p className="text-xs text-slate-400 font-medium uppercase tracking-widest">
                  Personalized Results
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 rounded-lg text-slate-400 hover:text-white hover:bg-white/10 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6 max-h-[60vh] overflow-y-auto">
            {/* Upload Area */}
            <label
              htmlFor="mission-file-input"
              className={cn(
                'block border-2 border-dashed rounded-xl p-8 text-center transition-all',
                isProcessing
                  ? 'border-violet-500/50 bg-violet-500/10 cursor-wait'
                  : 'border-white/20 hover:border-violet-500/50 hover:bg-white/5 cursor-pointer'
              )}
            >
              <input
                id="mission-file-input"
                type="file"
                ref={fileInputRef}
                className="hidden"
                accept=".pdf,.docx,.doc,.txt,.xlsx,.xls,.csv,.jpg,.jpeg,.png"
                multiple
                onChange={handleFileSelect}
                disabled={isProcessing}
              />
              
              {isProcessing ? (
                <div className="flex flex-col items-center gap-3">
                  <Loader2 className="w-10 h-10 text-violet-400 animate-spin" />
                  <p className="text-sm text-white font-medium">
                    Processing {processingFile}...
                  </p>
                </div>
              ) : (
                <>
                  <Upload className="w-10 h-10 text-slate-500 mx-auto mb-4" />
                  <p className="text-white font-medium mb-2">
                    Drop files here or click to upload
                  </p>
                  <p className="text-xs text-slate-400">
                    PDF, DOCX, TXT, Excel, CSV, Images
                  </p>
                </>
              )}
            </label>

            {/* Character Counter */}
            <div className="mt-4 flex justify-between items-center text-xs">
              <span className="text-slate-400">Context Size</span>
              <span className={cn(
                'font-mono font-bold',
                totalCharacters > maxCharacters * 0.9 
                  ? 'text-red-400' 
                  : totalCharacters > maxCharacters * 0.7 
                    ? 'text-amber-400' 
                    : 'text-emerald-400'
              )}>
                {(totalCharacters / 1000).toFixed(1)}k / {(maxCharacters / 1000).toFixed(0)}k chars
              </span>
            </div>

            {/* Error Display */}
            {error && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="mt-4 p-3 rounded-lg bg-red-500/10 border border-red-500/30 flex items-center gap-3"
              >
                <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0" />
                <p className="text-sm text-red-300">{error}</p>
                <button
                  onClick={() => setError(null)}
                  className="ml-auto text-red-400 hover:text-red-300"
                >
                  <X className="w-4 h-4" />
                </button>
              </motion.div>
            )}

            {/* Documents List */}
            {documents.length > 0 && (
              <div className="mt-6">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-sm font-bold text-white uppercase tracking-wider">
                    Uploaded Documents ({documents.length})
                  </h3>
                  <button
                    onClick={clearAllDocuments}
                    className="text-xs text-red-400 hover:text-red-300 flex items-center gap-1"
                  >
                    <Trash2 className="w-3 h-3" />
                    Clear All
                  </button>
                </div>
                
                <div className="space-y-2">
                  {documents.map((doc) => (
                    <motion.div
                      key={doc.id}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: 20 }}
                      className="flex items-center gap-3 p-3 rounded-lg bg-white/5 border border-white/10 group"
                    >
                      <div className={cn('flex-shrink-0', getFileTypeColor(doc.type))}>
                        {getFileTypeIcon(doc.type)}
                      </div>
                      
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-white font-medium truncate">
                          {doc.name}
                        </p>
                        <p className="text-xs text-slate-400">
                          {formatFileSize(doc.size)}
                          {doc.pageCount && ` • ${doc.pageCount} pages`}
                          {doc.extractedText
                            ? ` • ${(doc.extractedText.length / 1000).toFixed(1)}k chars`
                            : ' • re-upload needed'}
                        </p>
                      </div>
                      
                      <button
                        onClick={() => removeDocument(doc.id)}
                        className="p-2 rounded-lg text-slate-500 hover:text-red-400 hover:bg-red-500/10 opacity-0 group-hover:opacity-100 transition-all"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </motion.div>
                  ))}
                </div>
              </div>
            )}

            {/* Empty State */}
            {documents.length === 0 && !isProcessing && (
              <div className="mt-8 text-center">
                <p className="text-slate-500 text-sm">
                  No documents uploaded yet. Upload files to provide context for your conversation.
                </p>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="p-6 border-t border-white/10 flex justify-between items-center">
            <div className="flex items-center gap-2 text-xs text-slate-400">
              <AlertTriangle className="w-4 h-4" />
              <span>Documents provide context but don't override AI safety rules</span>
            </div>
            
            <button
              onClick={onClose}
              className="px-6 py-2.5 bg-gradient-to-r from-violet-500 to-fuchsia-500 text-white font-medium rounded-lg hover:from-violet-400 hover:to-fuchsia-400 transition-all flex items-center gap-2"
            >
              <Check className="w-4 h-4" />
              Done
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

export default MissionPanel;
